const DEFENSE_LAYERS = [
  { layer: '00', name: 'Cloudflare WAF', desc: 'DDoS protection, bot detection, IP reputation scoring at the edge.' },
  { layer: '01', name: 'TLS 1.3 Everywhere', desc: 'HTTPS with HSTS on every surface. All traffic encrypted in transit.' },
  { layer: '02', name: 'Rate Limiting', desc: 'Per-order and per-IP limits enforced at the Cloudflare layer.' },
  { layer: '03', name: 'API Authentication', desc: 'Per-merchant API keys plus Supabase JWT verification on every request.' },
  { layer: '04', name: 'Input Validation', desc: 'Strict typing and shape checks on every payload entering the API.' },
  { layer: '05', name: 'Row-Level Security', desc: 'Supabase RLS on every merchant table. Cross-merchant access is impossible by construction.' },
  { layer: '06', name: 'HMAC-Signed Webhooks', desc: 'Every webhook to your store is SHA-256 signed and verified with constant-time comparison.' },
  { layer: '07', name: 'On-Chain Verification', desc: 'Solana RPC parses each USDC transfer and validates amount and recipient before completing the order.' },
  { layer: '08', name: 'Non-Custodial Wallet', desc: 'Your seed phrase is generated client-side. Only the public address is stored. Funds settle to you, not us.' },
];

export default function SecuritySection() {
  return (
    <section id="security" className="bg-bone-white py-14 sm:py-20 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <span className="font-mono text-[10px] tracking-widest uppercase text-navy-500">
            Section III &mdash; Security
          </span>
          <div className="mt-2 w-full h-px bg-navy-200" />
        </div>

        <h2 className="font-serif text-navy-900 text-3xl md:text-4xl mb-4">
          End-to-end, signed and verified.
        </h2>
        <p className="font-sans text-navy-500 text-base max-w-2xl mb-12 leading-relaxed">
          Every payment passes through nine independent layers before your store sees it.
          A compromised layer cannot bypass the others.
        </p>

        <div className="space-y-5">
          {DEFENSE_LAYERS.map((layer) => (
            <div key={layer.layer} className="flex gap-4 items-start">
              <span className="font-mono text-2xl text-navy-600 font-medium flex-shrink-0 w-8">
                {layer.layer}
              </span>
              <div className="min-w-0">
                <h3 className="font-sans text-sm font-semibold text-navy-900 mb-0.5">
                  {layer.name}
                </h3>
                <p className="font-sans text-sm text-navy-500 leading-relaxed">
                  {layer.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-stretch w-full">
          <div className="hidden sm:flex items-stretch pr-3 md:pr-5 flex-shrink-0">
            <span className="font-serif text-5xl md:text-6xl text-navy-900 select-none leading-none flex items-center" style={{ fontWeight: 300 }}>{'{'}</span>
          </div>

          <div className="flex-1 py-4 md:py-5 border-t border-b sm:border-t-0 sm:border-b-0 sm:border-l sm:border-r border-navy-200 px-3 sm:px-5 md:px-8">
            <h3 className="font-sans text-sm font-semibold text-navy-900 mb-1">
              Defense in Depth
            </h3>
            <p className="font-sans text-xs text-navy-500 leading-relaxed">
              Each layer is independently enforced. The edge cannot grant access to the database, the API cannot bypass row-level security, and a forged webhook cannot complete an order without the matching on-chain transfer.
            </p>
          </div>

          <div className="hidden sm:flex items-stretch pl-3 md:pl-5 flex-shrink-0">
            <span className="font-serif text-5xl md:text-6xl text-navy-900 select-none leading-none flex items-center" style={{ fontWeight: 300 }}>{'}'}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
